import type { FC, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { motion, useReducedMotion, useScroll, useTransform } from 'motion/react'
import { EASE_OUT_EXPO } from '@/components/motion/reveal'
import { SignalField } from '@/components/motion/signal-field'
import { ProximityText } from '@/components/motion/proximity-text'
import { ScrambleText } from '@/components/motion/scramble-text'
import { ToneSection } from '@/components/generals/tone'
import { Cta } from '@/components/generals/cta'
import { DecisionCard } from '@/components/landing/home/decision-card'

const WORDS = ['Crypto', 'Gaming', 'Prepaid', 'Gift Cards']

const Line: FC<{ children: ReactNode, delay: number, className?: string }> = ({ children, delay, className }): ReactNode => (
    <span className={`block overflow-hidden pb-[0.04em] ${className ?? ''}`}>
        <motion.span
            className='block will-change-transform'
            initial={{ y: '108%' }}
            animate={{ y: '0%' }}
            transition={{ duration: 1.2, ease: EASE_OUT_EXPO, delay }}
        >
            {children}
        </motion.span>
    </span>
)

// A live field of transactions behind type that reacts to the cursor. As the
// visitor scrolls, "Risk" is struck through and carried out of the sentence,
// leaving an empty slot.
export const HomeHero: FC = (): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const reduce = useReducedMotion()
    const [index, setIndex] = useState(0)
    const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end end'] })

    const strike = useTransform(scrollYProgress, [0.04, 0.28], [0, 1])
    const riskX = useTransform(scrollYProgress, [0.3, 0.78], ['0%', '140%'])
    const riskOpacity = useTransform(scrollYProgress, [0.45, 0.78], [1, 0])
    const riskSkew = useTransform(scrollYProgress, [0.3, 0.78], [0, -12])
    const slotOpacity = useTransform(scrollYProgress, [0.6, 0.84], [0, 1])
    const noteY = useTransform(scrollYProgress, [0.66, 0.9], ['100%', '0%'])
    const cueOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0])
    const fieldScale = useTransform(scrollYProgress, [0, 1], reduce ? [1, 1] : [1, 1.25])
    const fieldOpacity = useTransform(scrollYProgress, [0.6, 1], reduce ? [1, 1] : [1, 0.35])
    const cardY = useTransform(scrollYProgress, [0, 1], reduce ? ['0%', '0%'] : ['0%', '-30%'])

    useEffect(() => {
        if (reduce) return

        const timer = window.setInterval(() => setIndex(value => (value + 1) % WORDS.length), 2600)

        return () => {
            window.clearInterval(timer)
        }
    }, [reduce])

    return (
        <ToneSection tone='void' id='hero'>
            <div ref={ref} className='relative h-[200vh]'>
                <div className='sticky top-0 flex h-svh min-h-[40rem] flex-col justify-between overflow-hidden px-5 pb-7 pt-24 md:px-8 lg:px-12 lg:pb-10 lg:pt-28'>
                    <motion.div
                        className='absolute inset-0 [mask-image:radial-gradient(ellipse_75%_70%_at_60%_45%,black_30%,transparent_100%)]'
                        style={{ scale: fieldScale, opacity: fieldOpacity }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1.6, delay: 0.2 }}
                    >
                        <SignalField accent='#8c5fff' />
                    </motion.div>

                    <div aria-hidden className='pointer-events-none absolute -right-40 top-1/4 size-[42rem] rounded-full bg-violet/20 blur-[140px]' />

                    <div className='relative z-10 flex items-start justify-between gap-6 t-label text-tone-muted'>
                        <ScrambleText text='Adaptive AI · Payment fraud prevention' delay={300} duration={1100} />
                        <motion.span className='hidden items-center gap-3 sm:flex' style={{ opacity: cueOpacity }}>
                            <span className='relative block h-10 w-px overflow-hidden bg-tone-line'>
                                <span className='absolute inset-x-0 top-0 h-1/2 bg-tone animate-[scroll-cue_1.8s_cubic-bezier(0.83,0,0.17,1)_infinite]' />
                            </span>
                            Scroll to take the risk out
                        </motion.span>
                    </div>

                    <h1 className='relative z-10 font-heading text-[clamp(3.25rem,min(12vw,17.5svh),13rem)] uppercase leading-[0.82] tracking-[-0.04em]'>
                        <span className='sr-only'>
                            Take the risk out of crypto, gaming, prepaid and gift cards
                        </span>

                        <span aria-hidden className='block'>
                            <Line delay={0.15}>
                                <ProximityText text='Take the' />
                            </Line>

                            <span className='block py-[0.02em] pl-[12vw] lg:pl-[10vw]'>
                                <span className='relative inline-grid align-top'>
                                    <motion.span
                                        className='col-start-1 row-start-1 flex items-center justify-start border border-dashed border-tone-line px-[0.12em]'
                                        style={{ opacity: reduce ? 0 : slotOpacity }}
                                    >
                                        <span className='block overflow-hidden'>
                                            <motion.span
                                                className='block whitespace-nowrap font-mono text-[clamp(0.6rem,0.9vw,0.8rem)] font-normal normal-case tracking-[0.12em] text-tone-accent'
                                                style={{ y: reduce ? '0%' : noteY }}
                                            >
                                                100% liability. Ours.
                                            </motion.span>
                                        </span>
                                    </motion.span>

                                    <span className='col-start-1 row-start-1 block overflow-x-visible overflow-y-clip pb-[0.04em]'>
                                        <motion.span
                                            className='block will-change-transform'
                                            initial={{ y: '108%' }}
                                            animate={{ y: '0%' }}
                                            transition={{ duration: 1.2, ease: EASE_OUT_EXPO, delay: 0.27 }}
                                        >
                                            <motion.span
                                                className='relative inline-block text-violet'
                                                style={reduce ? undefined : { x: riskX, opacity: riskOpacity, skewX: riskSkew }}
                                            >
                                                <ProximityText text='Risk' minWidth={112} maxWidth={125} minWeight={800} maxWeight={900} />
                                                <motion.span
                                                    className='absolute left-[-4%] top-[52%] h-[0.075em] w-[108%] origin-left bg-ember'
                                                    style={{ scaleX: reduce ? 0 : strike }}
                                                />
                                            </motion.span>
                                        </motion.span>
                                    </span>
                                </span>
                            </span>

                            <Line delay={0.39}>
                                <ProximityText text='Out of' />
                            </Line>

                            <span className='block overflow-hidden pb-[0.04em] pl-[6vw] lg:pl-[4vw]'>
                                <motion.span
                                    className='block will-change-transform'
                                    initial={{ y: '108%' }}
                                    animate={{ y: '0%' }}
                                    transition={{ duration: 1.2, ease: EASE_OUT_EXPO, delay: 0.51 }}
                                >
                                    <ScrambleText text={WORDS[index]} duration={700} className='font-semibold text-tone-accent wdth-75' />
                                </motion.span>
                            </span>
                        </span>
                    </h1>

                    <div className='relative z-10 flex flex-col gap-8 md:flex-row md:items-end md:justify-between'>
                        <motion.div
                            className='flex max-w-md flex-col gap-7'
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 1.1, ease: EASE_OUT_EXPO, delay: 0.75 }}
                        >
                            <p className='t-lead text-tone-muted'>
                                Book a call with our team to learn how nSure.ai delivers net incremental profit with zero risk.{' '}
                                <span className='font-serif text-[1.15em] italic text-tone'>
                                    It’s that simple.
                                </span>
                            </p>
                            <div>
                                <Cta href='/contact' cursor='Book'>
                                    Book a call
                                </Cta>
                            </div>
                        </motion.div>

                        <motion.div
                            className='hidden lg:block'
                            style={{ y: cardY }}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 1.2, ease: EASE_OUT_EXPO, delay: 0.9 }}
                        >
                            <DecisionCard />
                        </motion.div>
                    </div>
                </div>
            </div>
        </ToneSection>
    )
}
